import { AGE_GROUPS, GENDERS, WELLNESS_HUB_SLUG } from '../segments';
import {
  GLOBAL_WELLNESS_HUB_SLUG,
  getGlobalSegmentProfile,
  globalDietSlug,
} from './segments';
import {
  activeDayEatingTableGlobal,
  NONVEG_WEEK_DAYS_GLOBAL,
  VEG_WEEK_DAYS_GLOBAL,
} from './sharedMeals';

const DIET_HERO_VEG =
  'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1600&q=80';
const DIET_HERO_NONVEG =
  'https://images.unsplash.com/photo-1603360946369-dc9bb6258143?auto=format&fit=crop&w=1600&q=80';
const HUB_HERO =
  'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1600&q=80';

const BASE_PUBLISHED = new Date('2026-07-25T12:00:00.000Z').getTime();

function publishedOffset(index) {
  return new Date(BASE_PUBLISHED + index * 60_000).toISOString();
}

function buildGlobalDietPost(dietType, genderKey, ageKey, index) {
  const isVeg = dietType === 'vegetarian';
  const profile = getGlobalSegmentProfile(genderKey, ageKey);
  const audience = profile.audience;
  const slug = globalDietSlug(dietType, genderKey, ageKey);
  const otherDietSlug = globalDietSlug(isVeg ? 'non-vegetarian' : 'vegetarian', genderKey, ageKey);

  const dietLabel = isVeg ? 'Vegetarian (eggs OK)' : 'Non-vegetarian';
  const dietTitle = `7-Day ${isVeg ? 'Vegetarian' : 'Non-Vegetarian'} Healthy Diet for ${audience}`;

  return {
    id: `global-diet-${dietType}-${genderKey}-${ageKey}`,
    slug,
    title: dietTitle,
    excerpt: `${dietLabel} balanced meal plan for ${audience.toLowerCase()}—practical home cooking with protein, fiber, and whole foods. Works anywhere. Not medical advice.`,
    publishedAt: publishedOffset(index),
    coverImageUrl: isVeg ? DIET_HERO_VEG : DIET_HERO_NONVEG,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience,
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'diet',
    blogCategory: 'food-diet',
    wellnessMeta: {
      planType: isVeg ? 'vegetarian-diet' : 'nonveg-diet',
      gender: genderKey,
      ageKey,
      region: 'global',
    },
    relatedPosts: [
      {
        slug: otherDietSlug,
        title: `7-Day ${isVeg ? 'Non-Vegetarian' : 'Vegetarian'} Healthy Diet for ${audience}`,
        blurb: isVeg ? 'adds chicken and fish with the same structure.' : 'eggs and legumes if you skip meat.',
      },
      {
        slug: GLOBAL_WELLNESS_HUB_SLUG,
        title: 'Healthy Eating & Wellness Guide (All Ages)',
        blurb: 'pick another age or gender plan.',
      },
      {
        slug: WELLNESS_HUB_SLUG,
        title: 'Ohio Indian Health & Wellness Guide',
        blurb: 'Indian-style meal plans if you live in Ohio or prefer those flavors.',
      },
    ],
    sections: [
      {
        type: 'stats',
        ariaLabel: 'Plan at a glance',
        items: [
          { value: '7 days', label: 'Meal rotation' },
          { value: profile.proteinRange, label: 'Protein guide' },
          { value: audience, label: 'Written for' },
          { value: isVeg ? 'Eggs + legumes' : 'Chicken + fish', label: 'Anchors' },
        ],
      },
      {
        type: 'panel',
        title: 'Who this plan is for',
        paragraphs: [
          `${profile.dietWhoLead} You eat ${isVeg ? 'vegetarian food with eggs' : 'meat, fish, and eggs'} and want a realistic weekly menu without region-specific ingredients.`,
          'This is a general healthy-eating guide—not medical advice. Your doctor or dietitian should personalize portions if you manage diabetes, kidney disease, pregnancy, or heart conditions.',
        ],
        chips: [audience, dietLabel, 'Global', '7-day menu', 'Batch prep friendly'],
      },
      {
        type: 'panel',
        title: `Nutrition priorities for ${audience.toLowerCase()}`,
        list: profile.nutritionPriorities,
        callout:
          'If you take prescription medications or have food allergies, confirm this plan with a licensed clinician before changing how you eat.',
      },
      {
        type: 'panel',
        title: 'Step 1 — Daily eating template',
        orderedList: [
          'Breakfast: protein + complex carb + fruit.',
          'Mid-morning: fruit, nuts, or yogurt if lunch is later.',
          'Lunch: legumes or lean protein + whole grains + vegetables.',
          'Afternoon snack: fruit, yogurt, or a small handful of nuts.',
          'Dinner: vegetables + protein + moderate starch.',
          'Hydration: water through the day; extra in hot weather or after workouts.',
        ],
        callout: `Many adults in this group aim for about ${profile.proteinRange} of body weight per day in protein unless your clinician says otherwise. Spread protein across meals.`,
      },
      {
        type: 'panel',
        title: 'Step 2 — Universal shopping list',
        list: isVeg
          ? [
              'Proteins: eggs, Greek yogurt, tofu, tempeh, legumes (lentils, chickpeas, black beans), cheese.',
              'Grains: oats, brown rice, quinoa, whole-wheat bread or wraps, whole-grain pasta.',
              'Produce: leafy greens, broccoli, peppers, tomatoes, berries, bananas, citrus.',
              'Pantry: olive oil, nuts, seeds, spices, canned tomatoes, hummus.',
              'Optional: whey or plant protein for quick snacks or smoothies.',
            ]
          : [
              'Proteins: chicken breast, salmon/white fish, eggs, Greek yogurt, lean ground turkey.',
              'Grains: oats, brown rice, quinoa, whole-wheat bread, whole-grain pasta.',
              'Produce: spinach, broccoli, peppers, sweet potatoes, berries, bananas.',
              'Pantry: olive oil, beans, canned tomatoes, mustard, herbs and spices.',
            ],
      },
      {
        type: 'panel',
        title: 'Step 3 — Sunday batch prep (60–90 min)',
        orderedList: isVeg
          ? [
              'Cook a pot of lentils or beans; roast a tray of vegetables.',
              'Cook rice or quinoa in bulk.',
              'Hard-boil eggs; marinate tofu or prep cheese portions.',
              'Wash and chop salad vegetables for the week.',
            ]
          : [
              'Bake or grill chicken batches; portion fish for 2–3 days.',
              'Cook a pot of beans or lentils; cook grains in bulk.',
              'Hard-boil eggs; chop vegetables for stir-fries and salads.',
              'Prep snack containers (nuts, fruit, yogurt).',
            ],
      },
      {
        type: 'dayBlocks',
        title: 'Step 4 — Your 7-day menu',
        intro: profile.portionNote,
        days: isVeg ? VEG_WEEK_DAYS_GLOBAL : NONVEG_WEEK_DAYS_GLOBAL,
      },
      activeDayEatingTableGlobal(isVeg),
      {
        type: 'panel',
        title: 'Nutrients to watch year-round',
        list: isVeg
          ? [
              'B12 and iron: eggs, dairy, legumes; confirm with labs.',
              'Vitamin D—test and supplement per your doctor, especially in winter.',
              'Omega-3: walnuts, flax, chia; algae DHA if you eat no fish.',
            ]
          : [
              'Iron with vitamin C from lemon and vegetables.',
              'Vitamin D—test and supplement per your doctor.',
              'Omega-3: fatty fish twice weekly when possible.',
              'Sodium from restaurant and packaged food—cook at home when you can.',
            ],
      },
      {
        type: 'panel',
        title: 'Step 5 — Track what matters',
        paragraphs: [
          'Weekly weigh-in (same time), energy through the day, and how clothes fit—not daily scale noise.',
          'Aim for 80% home cooking. At restaurants, favor grilled proteins, salads, and vegetable sides; share heavy creamy dishes.',
        ],
      },
    ],
  };
}

export function buildAllGlobalDietPosts() {
  const posts = [];
  let index = 1;

  GENDERS.forEach((gender) => {
    AGE_GROUPS.forEach((age) => {
      posts.push(buildGlobalDietPost('vegetarian', gender.key, age.key, index++));
      posts.push(buildGlobalDietPost('non-vegetarian', gender.key, age.key, index++));
    });
  });

  return posts;
}

export function buildGlobalWellnessHubPost(allPosts) {
  const dietVeg = allPosts.filter((p) => p.wellnessMeta?.planType === 'vegetarian-diet');
  const dietNonVeg = allPosts.filter((p) => p.wellnessMeta?.planType === 'nonveg-diet');

  return {
    id: 'global-wellness-hub',
    slug: GLOBAL_WELLNESS_HUB_SLUG,
    title: 'Healthy Eating & Wellness Guide (All Ages & Genders)',
    excerpt:
      'Choose a 7-day healthy diet for your age and gender—vegetarian (eggs OK) or non-vegetarian. Practical meals for visitors worldwide. Not medical advice.',
    publishedAt: publishedOffset(0),
    coverImageUrl: HUB_HERO,
    authorName: 'Daily Sports Life',
    clubName: 'Player Wellness',
    audience: 'All adults',
    featured: true,
    component: 'WellnessGuideBlogArticle',
    theme: 'diet',
    blogCategory: 'food-diet',
    wellnessMeta: { planType: 'hub', region: 'global' },
    sections: [
      {
        type: 'panel',
        title: 'How to use this library',
        paragraphs: [
          'Pick the guide that matches your age and gender. Meal plans use familiar ingredients you can find in most supermarkets worldwide—Mediterranean bowls, grain salads, lean proteins, and batch-prep friendly dinners.',
          'Looking for Indian-style meals in Ohio? See our Ohio Indian wellness hub for region-specific shopping and flavors.',
          'Open the medical & legal disclaimer at the bottom of any plan for full terms. Work with licensed professionals for medical, pregnancy, or injury needs.',
        ],
        chips: ['Women & men', 'Ages 18–34, 35–49, 50+', 'Veg & non-veg', 'Global'],
      },
      {
        type: 'hubLinks',
        title: 'All meal plans',
        groups: [
          {
            title: 'Vegetarian 7-day meal plans (eggs OK)',
            links: dietVeg.map((p) => ({
              slug: p.slug,
              label: p.title,
              meta: p.audience,
            })),
          },
          {
            title: 'Non-vegetarian 7-day meal plans',
            links: dietNonVeg.map((p) => ({
              slug: p.slug,
              label: p.title,
              meta: p.audience,
            })),
          },
        ],
      },
      {
        type: 'panel',
        title: 'Regional guide for Ohio visitors',
        paragraphs: [
          'If you live in or near Powell, Dublin, Delaware, or Columbus and prefer Indian home cooking with local grocery tips, open the Ohio Indian Health & Wellness Guide from the related articles below.',
        ],
      },
    ],
    referenceLinks: [
      {
        label: 'Ohio Indian Health & Wellness Guide (regional meal plans)',
        url: `https://www.dailysportslife.com/blog/${WELLNESS_HUB_SLUG}`,
      },
      {
        label: 'CDC — healthy eating for adults',
        url: 'https://www.cdc.gov/healthyweight/healthy_eating/index.html',
      },
    ],
  };
}

export const GLOBAL_WELLNESS_GENERATED_POSTS = (() => {
  const generated = buildAllGlobalDietPosts();
  const hub = buildGlobalWellnessHubPost(generated);
  return [hub, ...generated];
})();

export function resolveGlobalWellnessSlug(slug) {
  return slug;
}
